import React, { useState, useEffect, useMemo } from 'react';
import colors from './../styles/Colors';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';
import { Visibility, Delete, Add } from '@mui/icons-material';
import LoadingSpinner from './common/LoadingSpinner';
import { apiService } from './../services/api/ApiService';
import { SelectChangeEvent } from '@mui/material/Select';

interface Transcripcion {
  id: number;
  name: string;
  numberCase: string;
  userId: number;
  status: 'COMPLETED' | 'PROCESSING' | 'ERROR' | 'IN_PROGRESS' | 'HIDDEN' | 'FAILED';
  updatedAt: string;
}

interface User {
  id: number;
  name: string;
  lastName: string;
}

interface AllTranscriptionsProps {
  setActiveItem: (item: string) => void;
}

type Order = 'asc' | 'desc';

const AllTranscriptions: React.FC<AllTranscriptionsProps> = ({ setActiveItem }) => {
  const [transcripciones, setTranscripciones] = useState<Transcripcion[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | string>(''); // Filtro por usuario
  const [searchTerm, setSearchTerm] = useState<string>(''); // Filtro por término de búsqueda

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [transcripcionToDelete, setTranscripcionToDelete] = useState<Transcripcion | null>(null);
  const [confirmName, setConfirmName] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 🚫 sin sort activo por defecto
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Transcripcion | null>(null);

  const navigate = useNavigate();

  // Obtener el perfil del usuario para verificar si es supervisor
  const userProfile = JSON.parse(localStorage.getItem('userProfile') || 'null');
  const isSupervisor = userProfile?.role === 'Supervisor';

  useEffect(() => {
    // const fetchTranscriptions = async () => {
    //   try {
    //     setLoading(true);
    //     const data = await apiService.getAdminTranscriptions();
    //     setTranscripciones(data);
    //     setFilteredTranscriptions(data); // Inicialmente mostrar todas las transcripciones
    //   } catch (err) {
    //     setError('Error fetching transcriptions');
    //   } finally {
    //     setLoading(false);
    //   }
    // };

    const fetchTranscriptions = async () => {
      try {
        setLoading(true);
        const data = await apiService.getAdminTranscriptions();
        setTranscripciones(data);
      } catch (err: any) {
        if (err.message === 'API Error: 403 - {"message":"Forbidden"}') {
          localStorage.clear();
          sessionStorage.clear();
          navigate('/session-closed');
        } else {
          setError('Error fetching transcriptions');
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const data = await apiService.getUsers();
        setUsers(data);
      } catch (err: any) {
        if (err.message === 'API Error: 403 - {"message":"Forbidden"}') {
          localStorage.clear();
          sessionStorage.clear();
          navigate('/session-closed');
        } else {
          setError('Error fetching users');
        }
      }
    };

    fetchTranscriptions();
    fetchUsers();
  }, [navigate]);

  // Filtrado base (usuario + búsqueda). NO ordenamos aquí: solo filtramos.
  const filtered = useMemo(() => {
    let f = transcripciones;

    if (selectedUserId) {
      f = f.filter(t => t.userId === Number(selectedUserId));
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      f = f.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.numberCase.toLowerCase().includes(q) ||
        getUserName(t.userId, users).toLowerCase().includes(q)
      );
    }

    return f;
  }, [transcripciones, selectedUserId, searchTerm, users]);

  // Ordenamiento (solo si el usuario activó sort)
  const rowsToRender = useMemo(() => {
    if (!orderBy) return filtered; // respeta el orden del backend
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;

      if (orderBy === 'updatedAt') {
        const va = new Date(a.updatedAt).getTime();
        const vb = new Date(b.updatedAt).getTime();
        cmp = va - vb;
      } else if (orderBy === 'status') {
        // Orden lexicográfico por status
        cmp = a.status.localeCompare(b.status);
      } else {
        // name o numberCase
        const va = String(a[orderBy] ?? '').toLowerCase();
        const vb = String(b[orderBy] ?? '').toLowerCase();
        cmp = va < vb ? -1 : va > vb ? 1 : 0;
      }

      return order === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filtered, orderBy, order]);

  const pageRows = useMemo(
    () => rowsToRender.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [rowsToRender, page, rowsPerPage]
  );

  const handleRequestSort = (property: keyof Transcripcion) => {
    if (orderBy === property) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrder('asc');
      setOrderBy(property);
    }
  };

  const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSING':
        return { background: '#FFE082', color: '#FF8F00', label: 'En proceso' };
      case 'ERROR':
        return { background: '#FFCDD2', color: '#D32F2F', label: 'Error' };
      case 'FAILED':
        return { background: '#FFCDD2', color: '#D32F2F', label: 'Error' };
      case 'COMPLETED':
        return { background: '#C6F6D5', color: '#2e7d32', label: 'Completado' };
      case 'IN_PROGRESS':
        return { background: '#BBDEFB', color: '#1976d2', label: 'En progreso' };
      case 'HIDDEN':
        return { background: '#E0E0E0', color: '#757575', label: 'Oculto' };
      default:
        return { background: '#F5F5F5', color: '#9E9E9E', label: 'Desconocido' };
    }
  };

  const handleOpenDeleteModal = (transcripcion: Transcripcion) => {
    setTranscripcionToDelete(transcripcion);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setTranscripcionToDelete(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (transcripcionToDelete && confirmName === transcripcionToDelete.name) {
      try {
        await apiService.updateTranscriptionById(transcripcionToDelete.id, { status: 'HIDDEN' });
        // Actualizar la lista de transcripciones removiendo la eliminada
        setTranscripciones(prev => prev.filter(t => t.id !== transcripcionToDelete.id));
        handleCloseDeleteModal();
      } catch (err: any) {
        if (err.message === 'API Error: 403 - {"message":"Forbidden"}') {
          localStorage.clear();
          sessionStorage.clear();
          navigate('/session-closed');
        } else {
          alert('Error al eliminar la transcripción. Por favor, intenta de nuevo.');
        }
      }
    } else {
      alert('El nombre de la transcripción no coincide.');
    }
  };

  // const handleViewDetails = (transcripcion: Transcripcion) => {
  //   setActiveItem(`Vista Transcripciones:${transcripcion.id}`);
  // };

  const handleViewDetails = (transcripcion: Transcripcion) => {
    if (transcripcion.status !== 'PROCESSING') {
      navigate(`/transcriptor/${transcripcion.id}`);
    }
  };

  function getUserName(userId: number, list: User[]) {
    const u = list.find(user => user.id === userId);
    return u ? `${u.name} ${u.lastName}` : 'Desconocido';
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <Typography color="error">{error}</Typography>;

  const totalPages = Math.ceil(rowsToRender.length / rowsPerPage);
  const start = rowsToRender.length ? (page - 1) * rowsPerPage + 1 : 0;
  const end = rowsToRender.length ? Math.min(page * rowsPerPage, rowsToRender.length) : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          fontFamily: "Montserrat",
          fontSize: "40px",
          fontWeight: 600,
          lineHeight: "48.76px",
          textAlign: "left",
        }}
        gutterBottom
      >
        Transcripciones
      </Typography>

      <Typography variant="body2" color="textSecondary" paragraph>
        En esta sección, podrás ver todas las transcripciones que has realizado. Utiliza la barra de búsqueda para
        encontrar transcripciones por nombre, número de expediente u otros criterios. Para visualizar una transcripción,
        haz clic en el ícono del ojo. Si deseas eliminar alguna, simplemente haz clic en el ícono de la papelera.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          placeholder="Busca por nombre, número de expediente, transcriptor, instancia, palabras clave, etc."
          variant="outlined"
          fullWidth
          sx={{ mr: 2 }}
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
        />
        {!isSupervisor && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/newTranscriptor')}
            sx={{
              backgroundColor: colors.colorPrimary,
              '&:hover': {
                backgroundColor: colors.colorPrimaryHover,
              },
            }}
          >
            Nueva Transcripción
          </Button>
        )}

        {/* Si quieres mostrar filtro por usuario, descomenta y llena users */}
        {/*
        <Select
          value={selectedUserId}
          onChange={(e) => { setSelectedUserId(e.target.value as number | ''); setPage(1); }}
          displayEmpty
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=""><em>Todos los usuarios</em></MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.name} {user.lastName}
            </MenuItem>
          ))}
        </Select>
        */}
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabla de transcripciones">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                  hideSortIcon
                >
                  Nombre
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'numberCase' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'numberCase'}
                  direction={orderBy === 'numberCase' ? order : 'asc'}
                  onClick={() => handleRequestSort('numberCase')}
                  hideSortIcon
                >
                  Número de expediente
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'updatedAt' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                  hideSortIcon
                >
                  Fecha de actualización
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                  hideSortIcon
                >
                  Estado
                </TableSortLabel>
              </TableCell>

              <TableCell>Transcriptor</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                <TableCell colSpan={6}>
                  <Typography align="center">Sin transcripciones...</Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell>{row.numberCase}</TableCell>
                  <TableCell>{new Date(row.updatedAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {row.status === 'PROCESSING' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', animation: 'blink 1.5s infinite' }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 1 }}>Procesando...</Typography>
                      </Box>
                    ) : (
                      <Chip
                        label={getStatusColor(row.status).label}
                        sx={{
                          backgroundColor: getStatusColor(row.status).background,
                          color: getStatusColor(row.status).color,
                        }}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>{getUserName(row.userId, users)}</TableCell>
                  <TableCell>
                    <IconButton disabled={row.status === 'PROCESSING' || row.status === 'HIDDEN' || row.status === 'FAILED'} onClick={() => handleViewDetails(row)} sx={{ color: 'black' }}>
                      <Visibility />
                    </IconButton>
                    {!isSupervisor && (
                      <IconButton onClick={() => handleOpenDeleteModal(row)} sx={{ color: 'red' }}>
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {rowsToRender.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Select value={rowsPerPage} onChange={handleChangeRowsPerPage} sx={{ mr: 1 }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
            <Typography variant="body2">Transcripciones por página</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {start}-{end} de {rowsToRender.length} transcripciones
            </Typography>
            <Pagination
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        </Box>
      )}

      <Dialog open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <DialogTitle>Eliminar transcripción</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro de que quieres eliminar esta transcripción: <strong>{transcripcionToDelete?.name}</strong>?
          </Typography>
          <TextField
            fullWidth
            label="Escribe el nombre de la transcripción para confirmar"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} sx={{ backgroundColor: 'grey', color: 'white' }}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} sx={{ backgroundColor: 'red', color: 'white' }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllTranscriptions;
